<template>
  <div class="results8-wrap">

    <div class="topbar">
      <div class="topbar-left">
        <span class="topbar-emoji">💪</span>
        <span><strong>SEQ</strong> · Your Body's Food Adventure</span>
      </div>
      <div>CLASS 7B · 09.05.2026</div>
    </div>

    <!-- HERO -->
    <div class="hero">
      <div class="float float-1">🍎</div>
      <div class="float float-2">💪</div>
      <div class="float float-3">🧠</div>
      <div class="float float-4">⚡</div>

      <span class="hero-tag">⚡ Your Body Adventure ⚡</span>
      <h1 class="hero-title">
        <span class="word-1">What</span> <span class="word-2">your</span> <span class="word-3">lunch</span><br>
        is <em>doing</em> to <em>your body</em>.
      </h1>
      <p class="hero-sub">
        Spoiler: it's wild. Every meal you eat builds bones, fuels brain cells, makes muscle, and even decides your mood for the afternoon. Let's see what's actually happening in there.
      </p>

      <div class="hero-class-card">
        <div class="hero-class-card-num">22</div>
        <div class="hero-class-card-label">
          <strong>students in 7B</strong>
          took the quiz and made this report
        </div>
      </div>

      <div>
        <span class="pair-badge">📘 Part 1 of 2 — companion to: What your lunch is doing to the world</span>
      </div>
    </div>

    <!-- 01 BIG NUMBERS -->
    <section class="section bg-orange">
      <div class="section-inner">
        <span class="section-num">📊 The Big Body Numbers</span>
        <h2 class="section-title">
          Your body is doing <em class="pop-sun">crazy stuff</em> with your food. Right now.
        </h2>
        <p class="section-lede">
          You ate breakfast a few hours ago. Most of you don't think about it again. But your body has been working on it non-stop ever since. Here are some numbers that show just how busy you are inside.
        </p>

        <div class="big-stats-row">
          <div v-for="s in bigStats" :key="s.label" class="big-stat">
            <div class="big-stat-emoji">{{ s.emoji }}</div>
            <div class="big-stat-value" :class="s.color">{{ s.value }}</div>
            <div class="big-stat-label">
              <strong>{{ s.label }}</strong>
              {{ s.body }}
            </div>
          </div>
        </div>

        <div class="poster-number">7+</div>
        <p class="poster-caption">
          cm you'll grow in the next year (roughly). All of that has to come from <em class="poster-caption-em">what you eat</em>. No food = no growth. Simple as that.
        </p>
      </div>
    </section>

    <!-- 02 COMIC STRIP -->
    <section class="section bg-paper">
      <div class="section-inner">
        <span class="section-num">🎬 Comic Strip</span>
        <h2 class="section-title">
          The <em class="pop-tomato">epic journey</em> of one bite of pasta.
        </h2>
        <p class="section-lede">
          You eat. End of story, right? Not even close. Here's what actually happens to one bite of pasta from "yum" to "yes I am now powered up."
        </p>

        <div class="comic-strip">
          <div class="comic-row">
            <div v-for="(panel, i) in comicPanels" :key="i" class="comic-panel">
              <div class="comic-panel-num">{{ i + 1 }}</div>
              <div class="comic-panel-emoji">{{ panel.emoji }}</div>
              <div class="comic-panel-text">
                <strong>{{ panel.title }}</strong><br>
                {{ panel.body }}
              </div>
            </div>
          </div>
        </div>

        <div class="divider">⭐ ⭐ ⭐</div>

        <p class="cycle-caption">
          <strong>You're not what you eat exactly.</strong><br>
          But every 7 years, almost every cell in your body has been replaced. <strong class="hot">All from food.</strong><br>
          The you of 7 years from now is literally being assembled, right now, by your kitchen.
        </p>
      </div>
    </section>

    <!-- 03 NUTRIENT TRADING CARDS -->
    <section class="section bg-dark">
      <div class="section-inner">
        <span class="section-num">⚡ Meet the Nutrients</span>
        <h2 class="section-title">
          <em class="pop-sun">Trading cards</em>: the superhero squad keeping you alive.
        </h2>
        <p class="section-lede">
          Forget the Avengers. Inside you, there's a squad of 6 absolute legends keeping your body running. Here they are, with their stats and where to find them.
        </p>

        <div class="cards-grid">
          <div v-for="n in nutrients" :key="n.name" class="nutrient-card">
            <div class="nutrient-card-header" :class="n.headerClass">
              <div class="nutrient-card-flag">{{ n.flag }}</div>
              <div class="nutrient-card-rarity" :class="n.legendary ? 'legendary' : ''">{{ n.rarity }}</div>
            </div>
            <div class="nutrient-card-body">
              <div class="nutrient-avatar">{{ n.avatar }}</div>
              <div>
                <div class="nutrient-name">{{ n.name }}</div>
                <div class="nutrient-role">{{ n.role }}</div>
                <div class="nutrient-stats">
                  <div v-for="(stat, i) in n.stats" :key="i" class="nutrient-stat">
                    <div class="nutrient-stat-num">{{ stat.num }}</div>
                    <div class="nutrient-stat-label">{{ stat.label }}</div>
                  </div>
                </div>
              </div>
            </div>
            <div class="nutrient-card-foods">
              <strong>Find it in:</strong>
              {{ n.foods }}
            </div>
          </div>
        </div>

        <p class="nutrient-footer">
          Collect them all by eating a variety of foods. No single hero saves the day. You need the whole squad.
        </p>
      </div>
    </section>

    <!-- 04 LIVE BODY WIDGET -->
    <section class="section bg-sky">
      <div class="section-inner">
        <span class="section-num">⏰ Live in Your Body</span>
        <h2 class="section-title">
          Right now, <em class="pop-sun">inside you</em>...
        </h2>
        <p class="section-lede">
          Your body is doing things every second of every day. Your kidneys, brain, gut, muscles, immune system — they're all working. Right this moment. Here's what's happening based on the time of day.
        </p>

        <div class="live-widget">
          <div class="live-place">
            <div class="live-place-emoji">🧠</div>
            <div class="live-place-name">Your Brain</div>
            <div class="live-place-time">{{ bodyClock.time }}</div>
            <div class="live-place-status">{{ bodyClock.brainStatus }}</div>
            <div class="live-place-action">{{ bodyClock.brainAction }}</div>
          </div>
          <div class="live-place">
            <div class="live-place-emoji">🦠</div>
            <div class="live-place-name">Your Gut</div>
            <div class="live-place-time">{{ bodyClock.time }}</div>
            <div class="live-place-status">{{ bodyClock.gutStatus }}</div>
            <div class="live-place-action">{{ bodyClock.gutAction }}</div>
          </div>
          <div class="live-place">
            <div class="live-place-emoji">💪</div>
            <div class="live-place-name">Your Muscles</div>
            <div class="live-place-time">{{ bodyClock.time }}</div>
            <div class="live-place-status">{{ bodyClock.muscleStatus }}</div>
            <div class="live-place-action">{{ bodyClock.muscleAction }}</div>
          </div>
        </div>

        <p class="live-footer">
          Your body never takes a break. Even right now while you're reading this, billions of tiny processes are running. Crazy, right?
        </p>
      </div>
    </section>

    <!-- 05 FOOD GROUPS -->
    <section class="section bg-leaf">
      <div class="section-inner">
        <span class="section-num">🍽️ The 7 Food Groups</span>
        <h2 class="section-title">
          Each group has a <em class="pop-sun">superpower</em>. Here's your class's score.
        </h2>
        <p class="section-lede">
          The Swiss Food Pyramid splits foods into 7 main groups. Each one does something specific for your body. Here's how your class is doing on all of them.
        </p>

        <div class="group-grid">
          <div v-for="g in groups" :key="g.name" class="group-tile" :class="{ 'is-average': g.isAverage }">
            <div v-if="!g.isAverage" class="group-score-badge" :class="g.badgeLevel">{{ g.score }}</div>
            <div class="group-emoji">{{ g.emoji }}</div>
            <div class="group-name">{{ g.name }}</div>
            <div class="group-superpower">{{ g.superpower }}</div>
          </div>
        </div>

        <p class="group-footer">
          Strong on water and veg. Plant proteins need backup, ASAP.
        </p>
      </div>
    </section>

    <!-- 06 DID YOU KNOW -->
    <section class="section bg-paper">
      <div class="section-inner">
        <span class="section-num">🤯 Mind-Blow Facts</span>
        <h2 class="section-title">
          6 things about <em class="pop-tomato">your body</em> that sound made up.
        </h2>
        <p class="section-lede">
          These are real. Tell your friends. Tell your parents. Watch them go "wait, really?"
        </p>

        <div class="dyk-grid">
          <div v-for="(d, i) in dyks" :key="d.title" class="dyk-card">
            <div class="dyk-badge">Fact #{{ i + 1 }}</div>
            <div class="dyk-emoji">{{ d.emoji }}</div>
            <div class="dyk-title">{{ d.title }}</div>
            <div class="dyk-body" v-html="d.body"></div>
          </div>
        </div>
      </div>
    </section>

    <!-- 07 POLLS -->
    <section class="section bg-pink">
      <div class="section-inner">
        <span class="section-num">📊 Class Poll</span>
        <h2 class="section-title">
          What <em class="pop-plum">your class</em> actually eats.
        </h2>
        <p class="section-lede">
          Anonymous results from the SEQ. No one knows who said what. But we know the patterns. Some answers are gonna surprise you.
        </p>

        <div v-for="poll in polls" :key="poll.question" class="poll-card">
          <div class="poll-question">{{ poll.question }}</div>
          <div v-for="(row, i) in poll.rows" :key="i" class="poll-bar-row">
            <div class="poll-bar-label">{{ row.label }}</div>
            <div class="poll-bar-track"><div class="poll-bar-fill" :class="row.cls" :style="{ width: row.width + '%' }"></div></div>
            <div class="poll-bar-count">{{ row.count }}</div>
          </div>
          <p class="poll-note">{{ poll.note }}</p>
        </div>
      </div>
    </section>

    <!-- 08 AI STORY -->
    <section class="section bg-paper">
      <div class="section-inner">
        <span class="section-num">✨ AI Magic</span>
        <h2 class="section-title">
          An <em class="pop-plum">AI</em> wrote your class a story.
        </h2>
        <p class="section-lede">
          We asked an AI to write a short story based on your real eating habits. Every class gets a different one.
        </p>

        <div class="ai-story">
          <span class="ai-story-tag">✨ Generated for Class 7B</span>
          <h3 class="ai-story-title">The Day Class 7B Discovered They Were Running on Empty</h3>
          <div class="ai-story-body">
            <p>It's 10am Tuesday. Period 3 math. Mr. Bühler is explaining fractions. <em>You can barely focus.</em></p>
            <p>Here's why: 9 of you skipped or barely ate breakfast. Your brain runs on glucose. Right now, your brain is texting your mouth: "Hey?? Hello?? Hungry??" <em>That's not laziness. That's biology.</em></p>
            <p>Meanwhile, the half of your class that ate proper breakfast? They're absolutely crushing this math problem. <em>Fuelled up = fired up.</em></p>
            <p>The good news: <em>your class drinks a LOT of water.</em> That's not nothing. Your brain is 75% water, and you keep it topped up. You also eat your vegetables, which is rare for your age. The fish situation is... let's say, an area for growth. (Get it? Growth?) Your brain literally wants more omega-3. Listen to it.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 09 BADGES -->
    <section class="section bg-plum">
      <div class="section-inner">
        <span class="section-num">🏆 Power Badges</span>
        <h2 class="section-title">
          Power-ups your class <em class="pop-sun">already unlocked</em>.
        </h2>
        <p class="section-lede">
          Based on your real answers, here's what your class has earned. Plus locked badges still up for grabs.
        </p>

        <div class="badges-grid">
          <div v-for="b in badges" :key="b.name" class="badge" :class="b.locked ? 'locked' : 'unlocked'">
            <div class="badge-emoji">{{ b.emoji }}</div>
            <div class="badge-name">{{ b.name }}</div>
            <div class="badge-desc">{{ b.desc }}</div>
            <div class="badge-status">{{ b.status }}</div>
          </div>
        </div>

        <p class="badges-footer">4 badges down, 4 to go. You're already halfway to peak you.</p>
      </div>
    </section>

    <!-- 10 SPIN WHEEL -->
    <section class="section bg-paper">
      <div class="section-inner">
        <span class="section-num">🎯 Class Challenge</span>
        <h2 class="section-title">
          Spin the <em class="pop-tomato">power-up wheel</em>.
        </h2>
        <p class="section-lede">
          Click the button. The wheel picks a random nutrition challenge for your class to try this week. Tiny actions, real effects.
        </p>

        <div class="wheel-card">
          <div class="wheel-emoji">🎯</div>
          <div class="wheel-title">POWER-UP GENERATOR</div>
          <div class="wheel-sub">Click. Get your class's nutrition mission.</div>
          <button class="wheel-button" @click="spinWheel">🎲 SPIN THE WHEEL</button>
          <div class="wheel-result">
            <span class="wheel-result-emoji">{{ wheelResult.emoji }}</span>
            {{ wheelResult.text }}
          </div>
        </div>
      </div>
    </section>

    <!-- 11 MISSIONS -->
    <section class="section bg-coral">
      <div class="section-inner">
        <span class="section-num">🚀 Choose Your Mission</span>
        <h2 class="section-title">
          Got <em class="pop-sun">a body</em>? Treat it like the upgrade it is.
        </h2>
        <p class="section-lede">
          Nine small things you can actually do. Each one is worth points. Pick whichever fits your style.
        </p>

        <div class="mission-grid">
          <div v-for="m in missions" :key="m.title" class="mission" :class="m.level">
            <div class="mission-emoji">{{ m.emoji }}</div>
            <div class="mission-title">{{ m.title }}</div>
            <div class="mission-body">{{ m.body }}</div>
            <span class="mission-points">{{ m.points }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- 12 RECIPE -->
    <section class="section bg-mint">
      <div class="section-inner">
        <span class="section-num">🍳 Try It</span>
        <h2 class="section-title">
          The <em class="pop-tomato">ultimate brain breakfast</em>.
        </h2>
        <p class="section-lede">
          Protein + slow carbs + fruit + healthy fat = your brain locked in for 4 hours. Built for school days. Takes 5 minutes.
        </p>

        <div class="recipe-card">
          <div>
            <span class="recipe-tag">EASY · 5 MIN · BRAIN FUEL</span>
            <h3 class="recipe-title">The Power Bowl</h3>
            <p class="recipe-sub">Greek yogurt + oats + fruit + nuts. The classic. Hits every nutrient your brain wants in the morning. Tastes like dessert. Is actually a complete meal.</p>

            <h4 class="recipe-h4">You need:</h4>
            <ul class="recipe-list">
              <li v-for="ing in ingredients" :key="ing">{{ ing }}</li>
            </ul>
          </div>
          <div>
            <h4 class="recipe-h4">Steps:</h4>
            <ol class="recipe-steps">
              <li v-for="step in steps" :key="step">{{ step }}</li>
            </ol>

            <div class="recipe-stats-box">
              <div class="recipe-stats-title">🧠 What it does for you:</div>
              <div class="recipe-stats-body">
                <strong>Calcium</strong> (bones) · <strong>Protein</strong> (muscle, focus) · <strong>Fiber</strong> (slow energy) · <strong>Vitamin C</strong> (immune) · <strong>Omega-3</strong> (if walnuts) · <strong>Slow carbs</strong> (brain fuel for 4 hours)
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 13 RECAP -->
    <section class="section bg-sun">
      <div class="section-inner">
        <span class="section-num">💪 The Real Point</span>
        <h2 class="section-title">
          You're literally <em class="pop-tomato">building</em> the adult you'll become.
        </h2>
        <p class="section-lede">
          Adults usually don't tell kids this. So we'll say it loud:
        </p>

        <div class="recap-banner">
          <div class="recap-emoji">⚡</div>
          <p class="recap-text">
            The food you eat from age 11 to 17 is doing more for your future body than anything you'll eat later. Your bones, your brain, your height, your strength. <em class="recap-em">All being built right now.</em>
          </p>
        </div>

        <div class="recap-grid">
          <div v-for="t in recapTiles" :key="t.title" class="recap-tile">
            <div class="recap-tile-emoji">{{ t.emoji }}</div>
            <div class="recap-tile-title">{{ t.title }}</div>
            <div class="recap-tile-body">{{ t.body }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- FOOTER -->
    <div class="footer">
      <div class="footer-emoji">💪🧠🦴⚡</div>
      <div class="footer-title">Class 7B, this is YOUR body's report.</div>
      <div class="footer-sub">
        The food you eat this year is being turned into the adult you'll be. Pretty wild responsibility. But also pretty wild superpower. Take the SEQ again in 3 months and see what's shifted.
      </div>
      <div class="footer-meta">SEQ · Swiss Eating Quiz · Part 1 of 2 · BFH · v0.1 · May 2026</div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'Results8',
  data() {
    return {
      timeInterval: null,
      bodyClock: {
        time: '—:—',
        brainStatus: 'Loading...',
        brainAction: '',
        gutStatus: 'Loading...',
        gutAction: '',
        muscleStatus: 'Loading...',
        muscleAction: '',
      },
      wheelResult: {
        emoji: '👇',
        text: 'Click the button to power up!',
      },
      challenges: [
        { emoji: '🍳', text: 'Everyone eats a real breakfast tomorrow. Toast, fruit, yogurt — anything counts. Notice your focus in period 1.' },
        { emoji: '🐟', text: 'Try fish once this week. Sushi, salmon, fish sticks — all count. Your brain says thanks.' },
        { emoji: '🫘', text: 'Lentil challenge: everyone tries lentils, hummus, or beans once. Photo evidence in class chat.' },
        { emoji: '🥬', text: 'Eat 5 different colors of vegetables this week. Make it a competition with yourself.' },
        { emoji: '🌾', text: 'Switch one white bread/pasta meal to whole grain. Notice if you feel full longer.' },
        { emoji: '💧', text: 'Drink 6+ glasses of water every day this week. Track it. Watch your energy change.' },
        { emoji: '😴', text: 'Aim for 9 hours of sleep for 3 nights this week. Phones away by 9:30pm. See what happens.' },
        { emoji: '🥚', text: 'Eat protein with every breakfast for a week (egg, yogurt, cheese, nuts). Feel less hungry by 10am.' },
        { emoji: '🌈', text: 'Eat at least 1 fruit AND 1 vegetable at lunch every day this week. Easy mode.' },
        { emoji: '🥜', text: 'Snack on a handful of nuts or seeds instead of crisps for one day. Tell us if you survive.' },
      ],
      bigStats: [
        { emoji: '🧬', value: '37', color: 'tomato', label: 'trillion cells', body: "Your body has 37 TRILLION cells. Every single one of them needs the right food to do its job. That's a lot of mouths to feed." },
        { emoji: '🦴', value: '90%', color: 'leaf', label: 'of your peak bone mass', body: "You'll build by age 20. The skeleton you'll carry your WHOLE LIFE is being built right now. Calcium = bone bricks." },
        { emoji: '🧠', value: '20%', color: 'sky', label: 'of all your energy', body: 'Goes to your brain. Just sitting there reading this, your brain is eating glucose like crazy. Skip breakfast and it notices.' },
      ],
      comicPanels: [
        { emoji: '😋', title: '0:00 · The bite.', body: 'You chew. Your saliva starts breaking the pasta down BEFORE you swallow. Saliva is already 100x sneakier than you thought.' },
        { emoji: '⚡', title: '0:30 · Stomach time.', body: "Acid stronger than battery acid melts the pasta into goo. Cool fact: your stomach makes new lining every few days so it doesn't digest itself." },
        { emoji: '🧬', title: '1-2 hr · The grand absorption.', body: 'Tiny finger-like things in your gut pull nutrients out of the goo. The carbs become glucose. The protein becomes amino acids. Pure magic.' },
        { emoji: '💪', title: '2-4 hr · Power up.', body: "The nutrients travel through your blood to every cell. Muscle cells get energy. Brain cells get fuel. You're now literally made of pasta. A little." },
      ],
      nutrients: [
        {
          headerClass: 'bg-protein', flag: '💪 PROTEIN', rarity: '★ THE BUILDER', legendary: false,
          avatar: '🦾', name: 'Protein',
          role: 'The brick-layer of your body. Builds muscle, repairs everything, makes hair grow.',
          stats: [
            { num: '50g', label: 'Daily for you' },
            { num: '20', label: 'Amino acid types' },
            { num: '100%', label: 'Of muscle' },
          ],
          foods: '🥚 Eggs · 🍗 Chicken · 🐟 Fish · 🥛 Yogurt · 🫘 Lentils · 🧀 Cheese · 🥜 Nuts',
        },
        {
          headerClass: 'bg-iron', flag: '🔴 IRON', rarity: '★★★ ENERGY KING', legendary: false,
          avatar: '⚔️', name: 'Iron',
          role: 'Carries oxygen in your blood. Low iron = tired, foggy, weak. THE energy nutrient.',
          stats: [
            { num: '11mg', label: 'Daily for you' },
            { num: '25k', label: 'Million blood cells' },
            { num: 'O₂', label: 'What it carries' },
          ],
          foods: '🥩 Red meat · 🥬 Spinach · 🫘 Lentils · 🍞 Whole grain · 🥚 Egg yolk · 🍫 Dark chocolate',
        },
        {
          headerClass: 'bg-calcium', flag: '🦴 CALCIUM', rarity: '★★★ BONE BUILDER', legendary: false,
          avatar: '🏗️', name: 'Calcium',
          role: 'Makes your bones strong and your teeth hard. Right now, your bones are growing FAST.',
          stats: [
            { num: '1300mg', label: 'Daily for you' },
            { num: '206', label: 'Bones in you' },
            { num: '99%', label: 'In skeleton' },
          ],
          foods: '🥛 Milk · 🧀 Cheese · 🥬 Broccoli · 🍶 Yogurt · 🐟 Sardines · 🌰 Almonds · 🍊 Fortified juice',
        },
        {
          headerClass: 'bg-fiber', flag: '🌾 FIBER', rarity: '★★ THE CLEANER', legendary: false,
          avatar: '🧹', name: 'Fiber',
          role: 'Feeds the GOOD bacteria in your gut. Keeps your insides moving. Yes, that means what you think.',
          stats: [
            { num: '25g', label: 'Daily for you' },
            { num: '100tr', label: 'Gut bacteria' },
            { num: '7m', label: 'Intestine length' },
          ],
          foods: '🍎 Apples · 🥦 Broccoli · 🍞 Whole bread · 🌽 Corn · 🫘 Beans · 🥕 Carrots · 🥑 Avocado',
        },
        {
          headerClass: 'bg-omega', flag: '🐟 OMEGA-3', rarity: '★★★★ BRAIN FOOD', legendary: true,
          avatar: '🧠', name: 'Omega-3',
          role: 'Your brain is literally made of fat. Specifically THIS fat. Helps focus, memory, mood.',
          stats: [
            { num: '1.6g', label: 'Daily for you' },
            { num: '60%', label: 'Of brain mass' },
            { num: '86bn', label: 'Brain neurons' },
          ],
          foods: '🐟 Salmon · 🐟 Sardines · 🌰 Walnuts · 🌱 Flaxseed · 🥚 Some eggs · 🫒 Chia seeds',
        },
        {
          headerClass: 'bg-vitc', flag: '🍊 VITAMIN C', rarity: '★ THE SHIELD', legendary: false,
          avatar: '🛡️', name: 'Vitamin C',
          role: 'Boosts your immune squad and helps you absorb iron. Fights off colds before they happen.',
          stats: [
            { num: '65mg', label: 'Daily for you' },
            { num: '100%', label: 'From food' },
            { num: '1', label: 'Orange covers it' },
          ],
          foods: '🍊 Oranges · 🍓 Strawberries · 🥦 Broccoli · 🌶️ Bell peppers · 🥝 Kiwi · 🥬 Spinach',
        },
      ],
      groups: [
        { emoji: '💧', name: 'Drinks', score: 78, badgeLevel: '', superpower: 'Keeps your blood flowing & brain firing' },
        { emoji: '🥬', name: 'Vegetables', score: 72, badgeLevel: '', superpower: 'Vitamins, minerals & fiber HQ' },
        { emoji: '🍎', name: 'Fruit', score: 65, badgeLevel: 'warn', superpower: 'Energy + Vitamin C boost' },
        { emoji: '🥛', name: 'Dairy', score: 70, badgeLevel: '', superpower: 'Bone-building calcium central' },
        { emoji: '🥩', name: 'Animal proteins', score: 62, badgeLevel: 'warn', superpower: 'Muscle bricks + iron + B12' },
        { emoji: '🌾', name: 'Grains', score: 58, badgeLevel: 'warn', superpower: 'Brain fuel & all-day energy' },
        { emoji: '🌱', name: 'Plant proteins', score: 38, badgeLevel: 'weak', superpower: 'Muscle + iron + fiber combo' },
        { emoji: '📊', name: 'Your average', superpower: '67 / 100 — solid base, room to grow!', isAverage: true },
      ],
      dyks: [
        { emoji: '🧠', title: 'Your brain runs on sugar.', body: 'Your brain is only 2% of your body weight but uses <strong>20% of your energy</strong>. It burns about <strong>120 grams of glucose a day</strong>. When you skip breakfast and feel foggy in math class? That\'s literally your brain running out of fuel.' },
        { emoji: '🦠', title: 'You have more bacteria than human cells.', body: 'There are <strong>100 trillion bacteria</strong> in your gut — more than the number of cells in your whole body. Most are GOOD. They help you digest, make vitamins, and even affect your <strong>mood</strong>. Fiber feeds them. Sugar messes them up.' },
        { emoji: '🦴', title: "You're building your skeleton RIGHT NOW.", body: "By age 20, you'll have built <strong>90% of the bones you'll have for life</strong>. Calcium and Vitamin D today = strong bones at 60. <strong>Skipping milk now</strong> doesn't just affect this year. It affects 50 years from now." },
        { emoji: '😴', title: 'You grow while you sleep.', body: "Growth hormone is released mostly between <strong>10pm and 2am</strong>. Sleep less than 8 hours? You're literally cutting your growth time. And the protein you ate that day? Becomes muscle WHILE YOU SLEEP. Sleep = upgrade time." },
        { emoji: '😡', title: 'Hangry is real science.', body: 'When blood sugar drops, your body releases <strong>cortisol and adrenaline</strong> (the stress hormones). That literally makes you irritable. So when you "snap" at your sibling before lunch? That\'s biology. Not personality. (Still apologize.)' },
        { emoji: '🌈', title: 'Colorful plants = different nutrients.', body: 'Every color in fruits and vegetables = different vitamins and antioxidants. <strong>Red</strong> (tomatoes) = lycopene. <strong>Orange</strong> (carrots) = beta-carotene. <strong>Green</strong> (spinach) = iron + folate. <strong>Purple</strong> (blueberries) = anthocyanins. Eat the rainbow, get everything.' },
      ],
      polls: [
        {
          question: 'Do you eat breakfast every morning?',
          rows: [
            { label: '😴 Never', cls: 't', width: 15, count: 3 },
            { label: '😶 Sometimes', cls: 'o', width: 30, count: 6 },
            { label: '🙂 Most days', cls: '', width: 30, count: 6 },
            { label: '😍 Every day', cls: '', width: 25, count: 5 },
          ],
          note: '9 of you skip or barely eat breakfast. Your brain runs on glucose. Skipping breakfast = math class on empty.',
        },
        {
          question: 'How often do you eat fish?',
          rows: [
            { label: '😶 Never', cls: 't', width: 50, count: 10 },
            { label: '😐 Monthly', cls: 'o', width: 30, count: 6 },
            { label: '🙂 Weekly', cls: '', width: 15, count: 3 },
            { label: '😍 Multi/week', cls: '', width: 5, count: 1 },
          ],
          note: 'Half of you never eat fish. Fish has omega-3, which builds brain cells. Your brain might want a chat.',
        },
        {
          question: 'How many hours of sleep do you get on school nights?',
          rows: [
            { label: '😵 Less than 7h', cls: 't', width: 20, count: 4 },
            { label: '😐 7-8h', cls: 'o', width: 45, count: 9 },
            { label: '😴 8-9h', cls: '', width: 30, count: 6 },
            { label: '💤 More than 9h', cls: '', width: 5, count: 1 },
          ],
          note: 'Your age needs 9-11 hours of sleep. Most of you are short. Growth hormone says hi.',
        },
      ],
      badges: [
        { locked: false, emoji: '💧', name: 'Hydro Hero', desc: 'Drinks water like a pro', status: '★ UNLOCKED' },
        { locked: false, emoji: '🥬', name: 'Veggie Champion', desc: 'Eats veg without drama', status: '★ UNLOCKED' },
        { locked: false, emoji: '🥛', name: 'Bone Builder', desc: 'Solid dairy = strong bones', status: '★ UNLOCKED' },
        { locked: false, emoji: '🚫🥤', name: 'Sugar Avoider', desc: 'Skips sweet drinks', status: '★ UNLOCKED (5 of you)' },
        { locked: true, emoji: '🍳', name: 'Breakfast Boss', desc: 'Eats breakfast daily', status: '🔒 LOCKED' },
        { locked: true, emoji: '🐟', name: 'Brain Booster', desc: 'Eats fish weekly', status: '🔒 LOCKED' },
        { locked: true, emoji: '🫘', name: 'Protein Pro', desc: 'Eats legumes regularly', status: '🔒 LOCKED' },
        { locked: true, emoji: '🌈', name: 'Rainbow Eater', desc: '5+ veg colors per week', status: '🔒 LOCKED' },
      ],
      missions: [
        { level: '', emoji: '🍌', title: 'Eat fruit at breakfast.', body: 'Banana, apple, berries — done. Quick win, extra Vitamin C, brain wakeup.', points: '+10 PTS · EASY' },
        { level: '', emoji: '🥚', title: 'Add protein to breakfast.', body: 'An egg. Some yogurt. Cheese on toast. Stays with you until lunch.', points: '+10 PTS · EASY' },
        { level: '', emoji: '💧', title: 'Water bottle at your desk.', body: 'Drink it through the day. 6 glasses = brain on hydration. Easy points.', points: '+10 PTS · EASY' },
        { level: 'medium', emoji: '🥗', title: 'Eat the rainbow this week.', body: '5+ different colors of fruit and veg. Different colors = different nutrients.', points: '+25 PTS · MEDIUM' },
        { level: 'medium', emoji: '😴', title: '9 hours of sleep, 3 nights.', body: "Phone off the bed. Growth hormone time. You'll wake up feeling like a different person.", points: '+25 PTS · MEDIUM' },
        { level: 'medium', emoji: '🐟', title: 'Try fish twice this month.', body: 'Fish fingers count. Sushi counts. Even tinned tuna. Omega-3 = literal brain food.', points: '+25 PTS · MEDIUM' },
        { level: 'hard', emoji: '🌱', title: 'No sweet drinks for one week.', body: 'Water, milk, unsweetened tea only. Your taste buds reset in about 4 days. Trust us.', points: '+50 PTS · HARD' },
        { level: 'hard', emoji: '🍞', title: 'Switch to whole grain for a week.', body: 'Whole grain bread, pasta, rice. More fiber, more energy, more long-lasting fullness.', points: '+50 PTS · HARD' },
        { level: 'hard', emoji: '🫘', title: 'Cook a legume meal yourself.', body: 'Lentil soup. Bean chili. Chickpea curry. You as a chef. Bonus: tell your class how it went.', points: '+50 PTS · HARD' },
      ],
      ingredients: [
        '🥣 1 cup Greek yogurt (or normal yogurt)',
        '🌾 1/3 cup oats (regular or instant)',
        '🍌 1 banana, sliced',
        '🍓 Handful of berries (fresh or frozen)',
        '🌰 1 tablespoon nuts or seeds',
        '🍯 Drizzle of honey (optional)',
      ],
      steps: [
        'Put yogurt in a bowl.',
        'Sprinkle oats on top.',
        'Add banana slices.',
        'Throw on berries.',
        'Sprinkle nuts/seeds.',
        'Honey if you want.',
        'Eat.',
      ],
      recapTiles: [
        { emoji: '🦴', title: 'Your bones are being built.', body: '90% of your skeleton mass by age 20.' },
        { emoji: '🧠', title: 'Your brain is wiring up.', body: 'More neurons connect now than at any other age.' },
        { emoji: '📈', title: "You're growing fast.", body: '7+ cm per year. Every bit of it from food.' },
      ],
    };
  },
  mounted() {
    this.updateBodyStatus();
    this.timeInterval = setInterval(this.updateBodyStatus, 60000);
  },
  beforeUnmount() {
    if (this.timeInterval) clearInterval(this.timeInterval);
  },
  methods: {
    updateBodyStatus() {
      const now = new Date();
      const h = now.getHours();
      const time = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

      let brainStatus, brainAction;
      if (h >= 6 && h < 10) {
        brainStatus = 'Waking up fast';
        brainAction = 'Glucose surge from breakfast = your sharpest hour.';
      } else if (h >= 10 && h < 14) {
        brainStatus = 'Peak focus';
        brainAction = 'Best time for hard tasks. Use this superpower wisely.';
      } else if (h >= 14 && h < 17) {
        brainStatus = 'Mid-afternoon dip';
        brainAction = 'Glucose dropping. A small snack would help.';
      } else if (h >= 17 && h < 21) {
        brainStatus = 'Winding down';
        brainAction = 'Memory consolidation starting. Studies less effective now.';
      } else {
        brainStatus = 'Memory mode';
        brainAction = "Sleep is when memories get filed away. Don't skip it.";
      }

      let gutStatus, gutAction;
      if (h >= 7 && h < 10) {
        gutStatus = 'Processing breakfast';
        gutAction = 'If you ate. If not, your blood sugar is low.';
      } else if (h >= 10 && h < 12) {
        gutStatus = 'Enzyme prep';
        gutAction = "Getting ready for lunch. You'll feel hungry soon.";
      } else if (h >= 12 && h < 15) {
        gutStatus = 'Digesting lunch';
        gutAction = 'Big workload. Blood is flowing to your gut.';
      } else if (h >= 15 && h < 18) {
        gutStatus = 'Absorbing nutrients';
        gutAction = 'Trillions of bacteria are munching on fiber.';
      } else if (h >= 18 && h < 21) {
        gutStatus = 'Dinner shift';
        gutAction = 'Breaking down evening meal. Big crew working.';
      } else {
        gutStatus = 'Repair mode';
        gutAction = 'Resting. Gut lining gets rebuilt at night.';
      }

      let muscleStatus, muscleAction;
      if (h >= 22 || h < 7) {
        muscleStatus = 'GROWTH MODE';
        muscleAction = 'Growth hormone peaking. This is when you actually grow.';
      } else {
        muscleStatus = 'Standing by';
        muscleAction = 'Using protein and glucose throughout the day.';
      }

      this.bodyClock = {
        time,
        brainStatus, brainAction,
        gutStatus, gutAction,
        muscleStatus, muscleAction,
      };
    },
    spinWheel() {
      this.wheelResult = { emoji: '⏳', text: 'Spinning...' };
      setTimeout(() => {
        const c = this.challenges[Math.floor(Math.random() * this.challenges.length)];
        this.wheelResult = { emoji: c.emoji, text: c.text };
      }, 800);
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Fredoka:wght@400;500;600;700&family=Caveat:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap');

.results8-wrap {
  --ink: #1A1A1A;
  --ink-soft: #2D2D2D;
  --paper: #FFF8EE;
  --card: #FFFFFF;
  --sun: #FFD93D;
  --tomato: #FF6B6B;
  --leaf: #6BCB77;
  --sky: #4D96FF;
  --plum: #9B59E2;
  --orange: #FF9F45;
  --pink: #FFB4D8;
  --mint: #B4E4D8;
  --coral: #FF8E72;

  --shadow-hard: 6px 6px 0 var(--ink);
  --shadow-medium: 4px 4px 0 var(--ink);
  --shadow-soft: 0 4px 12px rgba(0,0,0,0.08);

  font-family: 'Fredoka', sans-serif;
  font-size: 16px;
  line-height: 1.55;
  color: var(--ink);
  background: var(--paper);
  background-image:
    radial-gradient(circle at 15% 20%, rgba(255,107,107,0.10) 0%, transparent 40%),
    radial-gradient(circle at 85% 75%, rgba(255,159,69,0.10) 0%, transparent 40%);
  min-height: 100%;
}
.results8-wrap *,
.results8-wrap *::before,
.results8-wrap *::after {
  box-sizing: border-box;
}

/* TOPBAR */
.topbar {
  background: var(--ink);
  color: white;
  padding: 14px 28px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 500;
  font-size: 14px;
}
.topbar-left { display: flex; align-items: center; gap: 12px; }
.topbar-emoji {
  font-size: 28px;
  animation: pulse 2s ease-in-out infinite;
}
@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.15); }
}

/* HERO */
.hero {
  padding: 60px 32px 80px;
  text-align: center;
  position: relative;
  background:
    radial-gradient(circle at 20% 30%, var(--tomato) 0%, transparent 25%),
    radial-gradient(circle at 80% 70%, var(--orange) 0%, transparent 25%),
    radial-gradient(circle at 50% 50%, var(--sun) 0%, transparent 20%),
    var(--paper);
  overflow: hidden;
}
.hero-tag {
  display: inline-block;
  background: var(--ink);
  color: var(--tomato);
  font-weight: 600;
  font-size: 12px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  padding: 8px 18px;
  border-radius: 100px;
  margin-bottom: 24px;
  transform: rotate(2deg);
}
.hero-title {
  font-size: 76px;
  font-weight: 700;
  line-height: 0.95;
  letter-spacing: -0.03em;
  margin-bottom: 16px;
  position: relative;
  z-index: 2;
}
.hero-title .word-1 { color: var(--tomato); }
.hero-title .word-2 { color: var(--orange); }
.hero-title .word-3 { color: var(--plum); display: inline-block; transform: rotate(3deg); }
.hero-title em {
  font-family: 'Caveat', cursive;
  font-style: normal;
  color: var(--leaf);
  font-weight: 700;
}
.hero-sub {
  font-size: 22px;
  max-width: 620px;
  margin: 0 auto 36px;
  color: var(--ink-soft);
  line-height: 1.5;
  font-weight: 400;
}
.hero-class-card {
  background: white;
  border: 3px solid var(--ink);
  border-radius: 20px;
  padding: 20px 32px;
  display: inline-flex;
  align-items: center;
  gap: 20px;
  box-shadow: var(--shadow-hard);
  transform: rotate(1deg);
}
.hero-class-card-num {
  font-size: 48px;
  font-weight: 700;
  color: var(--tomato);
  line-height: 1;
}
.hero-class-card-label {
  text-align: left;
  font-size: 14px;
  color: var(--ink-soft);
}
.hero-class-card-label strong { font-size: 18px; color: var(--ink); display: block; }

.pair-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--leaf);
  color: white;
  border: 3px solid var(--ink);
  border-radius: 100px;
  padding: 8px 18px;
  font-weight: 600;
  font-size: 13px;
  margin-top: 24px;
  box-shadow: var(--shadow-medium);
  transform: rotate(-2deg);
}

.float {
  position: absolute;
  font-size: 56px;
  z-index: 1;
  filter: drop-shadow(2px 2px 0 rgba(0,0,0,0.1));
}
.float-1 { top: 80px; left: 8%; animation: float 5s ease-in-out infinite; }
.float-2 { top: 120px; right: 10%; animation: float 6s ease-in-out infinite reverse; }
.float-3 { bottom: 60px; left: 12%; animation: float 7s ease-in-out infinite; }
.float-4 { bottom: 80px; right: 14%; animation: float 5.5s ease-in-out infinite reverse; }
@keyframes float {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-12px) rotate(8deg); }
}

/* SECTIONS */
.section { padding: 60px 32px; position: relative; }
.section-inner { max-width: 920px; margin: 0 auto; }
.section.bg-sun { background: var(--sun); }
.section.bg-leaf { background: var(--leaf); color: white; }
.section.bg-sky { background: var(--sky); color: white; }
.section.bg-plum { background: var(--plum); color: white; }
.section.bg-coral { background: var(--coral); color: white; }
.section.bg-mint { background: var(--mint); }
.section.bg-pink { background: var(--pink); }
.section.bg-dark { background: var(--ink); color: white; }
.section.bg-paper { background: var(--paper); }
.section.bg-orange { background: var(--orange); color: white; }
.section.bg-tomato { background: var(--tomato); color: white; }

.section-num {
  display: inline-block;
  background: var(--ink);
  color: white;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  padding: 6px 14px;
  border-radius: 100px;
  margin-bottom: 16px;
}
.section.bg-dark .section-num { background: var(--tomato); color: white; }
.section.bg-leaf .section-num,
.section.bg-sky .section-num,
.section.bg-plum .section-num,
.section.bg-coral .section-num,
.section.bg-orange .section-num,
.section.bg-tomato .section-num { background: white; color: var(--ink); }
.section-title {
  font-size: 52px;
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.02em;
  margin-bottom: 16px;
  max-width: 800px;
}
.section-title em {
  font-family: 'Caveat', cursive;
  font-style: normal;
  font-weight: 700;
}
.section-title em.pop-tomato { color: var(--tomato); }
.section-title em.pop-leaf { color: var(--leaf); }
.section-title em.pop-sky { color: var(--sky); }
.section-title em.pop-sun { color: var(--sun); }
.section-title em.pop-plum { color: var(--plum); }
.section-title em.pop-orange { color: var(--orange); }
.section-lede {
  font-size: 19px;
  max-width: 700px;
  margin-bottom: 36px;
  opacity: 0.92;
  font-weight: 400;
}

/* BIG STATS */
.big-stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin: 36px 0;
}
.big-stat {
  background: white;
  border: 3px solid var(--ink);
  border-radius: 24px;
  padding: 32px 24px;
  text-align: center;
  box-shadow: var(--shadow-hard);
  position: relative;
}
.big-stat:nth-child(1) { transform: rotate(-1.5deg); }
.big-stat:nth-child(2) { transform: rotate(1deg); }
.big-stat:nth-child(3) { transform: rotate(-0.5deg); }
.big-stat-emoji { font-size: 56px; margin-bottom: 8px; line-height: 1; }
.big-stat-value {
  font-size: 56px;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 8px;
  letter-spacing: -0.02em;
}
.big-stat-value.tomato { color: var(--tomato); }
.big-stat-value.leaf { color: var(--leaf); }
.big-stat-value.sky { color: var(--sky); }
.big-stat-value.plum { color: var(--plum); }
.big-stat-value.orange { color: var(--orange); }
.big-stat-label {
  font-size: 15px;
  color: var(--ink-soft);
  line-height: 1.4;
}
.big-stat-label strong { display: block; font-size: 17px; color: var(--ink); margin-bottom: 4px; }

.poster-number {
  font-size: 200px;
  font-weight: 700;
  line-height: 0.9;
  letter-spacing: -0.05em;
  text-align: center;
  margin: 20px 0;
  color: var(--ink);
}
.poster-caption {
  text-align: center;
  font-size: 22px;
  font-weight: 500;
  max-width: 600px;
  margin: 0 auto;
}
.poster-caption-em {
  font-family: 'Caveat', cursive;
  color: var(--tomato);
  font-weight: 700;
  font-size: 28px;
  font-style: normal;
}

/* COMIC STRIP */
.comic-strip {
  background: white;
  border: 3px solid var(--ink);
  border-radius: 24px;
  padding: 24px;
  margin: 24px 0;
  box-shadow: var(--shadow-hard);
}
.comic-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}
.comic-panel {
  background: var(--paper);
  border: 2px solid var(--ink);
  border-radius: 12px;
  padding: 18px 14px;
  text-align: center;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
}
.comic-panel-emoji { font-size: 64px; line-height: 1; }
.comic-panel-num {
  background: var(--orange);
  color: white;
  font-weight: 700;
  font-size: 14px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  border: 2px solid var(--ink);
}
.comic-panel-text {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.35;
}
.divider {
  text-align: center;
  font-size: 36px;
  margin: 36px 0;
  letter-spacing: 0.2em;
}
.cycle-caption {
  font-size: 19px;
  text-align: center;
  max-width: 720px;
  margin: 0 auto;
  line-height: 1.5;
}
.cycle-caption .hot { color: var(--tomato); }

/* NUTRIENT CARDS */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}
.nutrient-card {
  background: white;
  color: var(--ink);
  border: 3px solid var(--ink);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: var(--shadow-hard);
  position: relative;
}
.nutrient-card:nth-child(odd) { transform: rotate(-0.5deg); }
.nutrient-card:nth-child(even) { transform: rotate(0.5deg); }
.nutrient-card-header {
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 3px solid var(--ink);
}
.nutrient-card-header.bg-protein { background: var(--tomato); color: white; }
.nutrient-card-header.bg-iron { background: var(--ink); color: white; }
.nutrient-card-header.bg-calcium { background: var(--mint); }
.nutrient-card-header.bg-fiber { background: var(--leaf); color: white; }
.nutrient-card-header.bg-omega { background: var(--sky); color: white; }
.nutrient-card-header.bg-vitc { background: var(--orange); color: white; }
.nutrient-card-flag {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.05em;
}
.nutrient-card-rarity {
  font-size: 10px;
  font-weight: 700;
  background: var(--sun);
  color: var(--ink);
  padding: 4px 10px;
  border-radius: 100px;
  letter-spacing: 0.1em;
}
.nutrient-card-rarity.legendary { background: var(--plum); color: white; }
.nutrient-card-body {
  padding: 20px;
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 16px;
  align-items: start;
}
.nutrient-avatar {
  width: 100px;
  height: 100px;
  border-radius: 20px;
  background: var(--paper);
  border: 2px solid var(--ink);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 60px;
  overflow: hidden;
}
.nutrient-name { font-size: 22px; font-weight: 700; margin-bottom: 4px; }
.nutrient-role { font-size: 13px; color: var(--ink-soft); margin-bottom: 12px; line-height: 1.4; font-style: italic; }
.nutrient-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  background: var(--paper);
  padding: 10px;
  border-radius: 10px;
  border: 2px solid var(--ink);
}
.nutrient-stat { text-align: center; }
.nutrient-stat-num {
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
}
.nutrient-stat-label {
  font-size: 10px;
  color: var(--ink-soft);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 2px;
}
.nutrient-card-foods {
  padding: 14px 20px;
  background: var(--paper);
  border-top: 2px solid var(--ink);
  font-size: 13px;
}
.nutrient-card-foods strong {
  display: block;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ink-soft);
  margin-bottom: 4px;
}
.nutrient-footer {
  margin-top: 36px;
  font-size: 19px;
  text-align: center;
  max-width: 720px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
}

/* LIVE WIDGET */
.live-widget {
  background: white;
  color: var(--ink);
  border: 3px solid var(--ink);
  border-radius: 24px;
  padding: 28px;
  box-shadow: var(--shadow-hard);
  margin: 24px 0;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px;
}
.live-place {
  text-align: center;
  padding: 18px;
  background: var(--paper);
  border: 2px solid var(--ink);
  border-radius: 16px;
}
.live-place-emoji { font-size: 48px; margin-bottom: 8px; line-height: 1; }
.live-place-name { font-weight: 700; font-size: 16px; margin-bottom: 4px; }
.live-place-time { font-size: 12px; color: var(--ink-soft); margin-bottom: 12px; font-family: monospace; }
.live-place-status {
  background: white;
  border: 2px solid var(--ink);
  border-radius: 10px;
  padding: 8px 12px;
  font-weight: 600;
  font-size: 14px;
}
.live-place-action {
  margin-top: 8px;
  font-size: 12px;
  color: var(--ink-soft);
  font-style: italic;
}
.live-footer {
  margin-top: 24px;
  font-size: 17px;
  text-align: center;
  opacity: 0.92;
  max-width: 640px;
  margin-left: auto;
  margin-right: auto;
}

/* FOOD GROUPS */
.group-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-top: 24px;
}
.group-tile {
  background: white;
  color: var(--ink);
  border: 3px solid var(--ink);
  border-radius: 16px;
  padding: 18px;
  box-shadow: var(--shadow-medium);
  text-align: center;
  position: relative;
}
.group-tile.is-average { background: var(--paper); border-style: dashed; }
.group-emoji {
  font-size: 44px;
  line-height: 1;
  margin-bottom: 10px;
}
.group-name {
  font-weight: 700;
  font-size: 15px;
  margin-bottom: 8px;
}
.group-superpower {
  font-size: 12px;
  color: var(--ink-soft);
  line-height: 1.4;
  font-style: italic;
}
.group-score-badge {
  position: absolute;
  top: -12px;
  right: -10px;
  background: var(--leaf);
  color: white;
  border: 2px solid var(--ink);
  border-radius: 100px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 700;
  transform: rotate(8deg);
}
.group-score-badge.warn { background: var(--orange); }
.group-score-badge.weak { background: var(--tomato); }
.group-footer {
  margin-top: 32px;
  font-size: 22px;
  text-align: center;
  font-family: 'Caveat', cursive;
  font-weight: 600;
}

/* DYK */
.dyk-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;
  margin-top: 24px;
}
.dyk-card {
  background: white;
  border: 3px solid var(--ink);
  border-radius: 20px;
  padding: 24px;
  box-shadow: var(--shadow-medium);
  position: relative;
}
.dyk-card:nth-child(4n+1) { transform: rotate(-1deg); }
.dyk-card:nth-child(4n+2) { transform: rotate(0.8deg); }
.dyk-card:nth-child(4n+3) { transform: rotate(0.6deg); }
.dyk-card:nth-child(4n+4) { transform: rotate(-0.7deg); }
.dyk-badge {
  position: absolute;
  top: -14px;
  left: 20px;
  background: var(--tomato);
  color: white;
  border: 2px solid var(--ink);
  padding: 4px 12px;
  border-radius: 100px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.dyk-emoji { font-size: 48px; margin-bottom: 12px; line-height: 1; }
.dyk-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 8px;
  line-height: 1.2;
}
.dyk-body { font-size: 14px; line-height: 1.5; color: var(--ink-soft); }
.dyk-body :deep(strong) { color: var(--ink); }

/* POLLS */
.poll-card {
  background: white;
  color: var(--ink);
  border: 3px solid var(--ink);
  border-radius: 24px;
  padding: 28px;
  box-shadow: var(--shadow-hard);
  margin: 24px 0;
}
.poll-question {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 16px;
  line-height: 1.2;
}
.poll-bar-row {
  display: grid;
  grid-template-columns: 140px 1fr 60px;
  gap: 14px;
  align-items: center;
  margin-bottom: 12px;
}
.poll-bar-label { font-weight: 500; font-size: 14px; }
.poll-bar-track {
  background: var(--paper);
  border: 2px solid var(--ink);
  border-radius: 100px;
  height: 28px;
  position: relative;
  overflow: hidden;
}
.poll-bar-fill {
  height: 100%;
  border-radius: 100px;
  background: var(--leaf);
  border-right: 2px solid var(--ink);
}
.poll-bar-fill.t { background: var(--tomato); }
.poll-bar-fill.s { background: var(--sky); }
.poll-bar-fill.p { background: var(--plum); }
.poll-bar-fill.o { background: var(--orange); }
.poll-bar-count {
  font-weight: 700;
  font-size: 15px;
  text-align: center;
}
.poll-note {
  margin-top: 14px;
  font-size: 14px;
  font-style: italic;
  color: var(--ink-soft);
}

/* AI STORY */
.ai-story {
  background: linear-gradient(135deg, var(--tomato) 0%, var(--orange) 100%);
  color: white;
  border: 3px solid var(--ink);
  border-radius: 28px;
  padding: 36px;
  box-shadow: var(--shadow-hard);
  margin: 24px 0;
  position: relative;
  overflow: hidden;
}
.ai-story::before {
  content: '✨';
  position: absolute;
  top: 20px;
  right: 20px;
  font-size: 60px;
  opacity: 0.4;
}
.ai-story-tag {
  display: inline-block;
  background: var(--sun);
  color: var(--ink);
  padding: 6px 16px;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 14px;
}
.ai-story-title {
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 14px;
  line-height: 1.15;
}
.ai-story-body {
  font-size: 17px;
  line-height: 1.65;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
}
.ai-story-body p { margin: 0 0 12px 0; }
.ai-story-body p:last-child { margin-bottom: 0; }
.ai-story-body em {
  font-family: 'Caveat', cursive;
  font-style: normal;
  color: var(--sun);
  font-weight: 700;
}

/* BADGES */
.badges-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-top: 24px;
}
.badge {
  background: white;
  color: var(--ink);
  border: 3px solid var(--ink);
  border-radius: 20px;
  padding: 20px 14px;
  text-align: center;
  box-shadow: var(--shadow-medium);
  position: relative;
}
.badge.unlocked { background: var(--sun); }
.badge.locked { opacity: 0.55; background: #E5E5E0; }
.badge-emoji { font-size: 48px; line-height: 1; margin-bottom: 8px; }
.badge-name {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  margin-bottom: 4px;
}
.badge-desc { font-size: 11px; color: var(--ink-soft); line-height: 1.3; }
.badge-status {
  margin-top: 8px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.badge.unlocked .badge-status { color: var(--leaf); }
.badge.locked .badge-status { color: var(--ink-soft); }
.badges-footer {
  margin-top: 28px;
  text-align: center;
  font-family: 'Caveat', cursive;
  font-weight: 600;
  font-size: 24px;
}

/* WHEEL */
.wheel-card {
  background: var(--leaf);
  color: white;
  border: 3px solid var(--ink);
  border-radius: 28px;
  padding: 36px;
  box-shadow: var(--shadow-hard);
  text-align: center;
  margin: 24px 0;
}
.wheel-emoji { font-size: 84px; margin-bottom: 14px; animation: spin 3s ease-in-out infinite; display: inline-block; }
@keyframes spin {
  0%, 100% { transform: rotate(0deg); }
  50% { transform: rotate(20deg); }
}
.wheel-title { font-size: 36px; font-weight: 700; margin-bottom: 8px; }
.wheel-sub { font-size: 17px; opacity: 0.9; margin-bottom: 24px; }
.wheel-button {
  background: var(--sun);
  color: var(--ink);
  border: 3px solid var(--ink);
  padding: 16px 40px;
  font-family: 'Fredoka', sans-serif;
  font-size: 20px;
  font-weight: 700;
  border-radius: 100px;
  cursor: pointer;
  box-shadow: var(--shadow-medium);
  transition: all 0.15s;
}
.wheel-button:hover { transform: translateY(-3px); box-shadow: 6px 6px 0 var(--ink); }
.wheel-button:active { transform: translateY(0); box-shadow: 2px 2px 0 var(--ink); }
.wheel-result {
  margin-top: 24px;
  background: white;
  color: var(--ink);
  padding: 20px 28px;
  border: 3px solid var(--ink);
  border-radius: 18px;
  display: inline-block;
  min-width: 320px;
  font-size: 18px;
  font-weight: 500;
}
.wheel-result-emoji { font-size: 36px; display: block; margin-bottom: 8px; }

/* MISSIONS */
.mission-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin: 24px 0;
}
.mission {
  background: white;
  color: var(--ink);
  border: 3px solid var(--ink);
  border-radius: 20px;
  padding: 22px;
  box-shadow: var(--shadow-medium);
  position: relative;
}
.mission-emoji { font-size: 44px; margin-bottom: 10px; line-height: 1; }
.mission-title {
  font-size: 17px;
  font-weight: 700;
  margin-bottom: 8px;
  line-height: 1.25;
}
.mission-body { font-size: 13px; color: var(--ink-soft); line-height: 1.5; margin-bottom: 14px; }
.mission-points {
  display: inline-block;
  background: var(--leaf);
  color: white;
  padding: 4px 10px;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 700;
  border: 2px solid var(--ink);
}
.mission.medium .mission-points { background: var(--orange); }
.mission.hard .mission-points { background: var(--tomato); }

/* RECIPE */
.recipe-card {
  background: white;
  color: var(--ink);
  border: 3px solid var(--ink);
  border-radius: 24px;
  padding: 32px;
  box-shadow: var(--shadow-hard);
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
}
.recipe-tag {
  display: inline-block;
  background: var(--orange);
  color: white;
  padding: 4px 12px;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
}
.recipe-title { font-size: 28px; font-weight: 700; margin: 14px 0 18px; line-height: 1.15; }
.recipe-sub { font-size: 14px; color: var(--ink-soft); margin-bottom: 18px; line-height: 1.55; }
.recipe-h4 {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 12px;
}
.recipe-list { list-style: none; padding: 0; font-size: 14px; line-height: 1.8; margin: 0; }
.recipe-steps { padding-left: 20px; font-size: 14px; line-height: 1.75; margin: 0; }
.recipe-stats-box {
  margin-top: 20px;
  background: var(--paper);
  border: 2px solid var(--ink);
  border-radius: 14px;
  padding: 14px 18px;
}
.recipe-stats-title {
  font-weight: 700;
  font-size: 14px;
  margin-bottom: 4px;
}
.recipe-stats-body {
  font-size: 13px;
  line-height: 1.5;
  color: var(--ink-soft);
}

/* RECAP */
.recap-banner {
  background: white;
  color: var(--ink);
  border: 3px solid var(--ink);
  border-radius: 28px;
  padding: 40px;
  box-shadow: var(--shadow-hard);
  margin: 24px 0;
}
.recap-emoji {
  font-size: 64px;
  text-align: center;
  margin-bottom: 16px;
}
.recap-text {
  font-size: 22px;
  line-height: 1.55;
  text-align: center;
  max-width: 700px;
  margin: 0 auto;
  font-weight: 500;
}
.recap-em {
  font-family: 'Caveat', cursive;
  font-style: normal;
  color: var(--tomato);
  font-weight: 700;
  font-size: 28px;
}
.recap-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;
  margin-top: 32px;
}
.recap-tile {
  background: white;
  color: var(--ink);
  border: 3px solid var(--ink);
  border-radius: 20px;
  padding: 24px;
  text-align: center;
  box-shadow: var(--shadow-medium);
}
.recap-tile-emoji {
  font-size: 48px;
  margin-bottom: 12px;
}
.recap-tile-title {
  font-weight: 700;
  font-size: 17px;
  margin-bottom: 6px;
}
.recap-tile-body {
  font-size: 13px;
  color: var(--ink-soft);
}

/* FOOTER */
.footer {
  background: var(--ink);
  color: white;
  padding: 48px 32px;
  text-align: center;
}
.footer-emoji { font-size: 56px; margin-bottom: 16px; }
.footer-title { font-size: 28px; font-weight: 700; margin-bottom: 12px; }
.footer-sub { font-size: 15px; opacity: 0.7; max-width: 480px; margin: 0 auto; line-height: 1.6; }
.footer-meta { font-size: 11px; opacity: 0.5; margin-top: 24px; letter-spacing: 0.1em; text-transform: uppercase; }
</style>
